import { useIsFocused, useRoute } from '@react-navigation/native';
import dayjs from 'dayjs';
import debounce from 'lodash.debounce';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, TouchableOpacity } from 'react-native';

import ActivityList from '@components/ActivityList/ActivityList';
import { ActivityTruncationBanner } from '@components/ActivityList/ActivityTruncationBanner';
import { DateRange } from '@components/DateRangePicker/DateRangePickerModal';
import { ContactType } from '@components/Invite/Contact';
import { ExportActivityTimesheetModal } from '@components/Modals/ExportActivityTimesheetModal';
import { Box, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import Icon from '@components/shared/Icon/Icon';
import SearchInput from '@components/shared/SearchInput/SearchInput.web';
import {
  ActivityRowSkeleton,
  SkeletonList,
} from '@components/shared/skeletons';
import {
  PANEL_WIDTH,
  PopupActivityFilterPanel,
} from '@components/Web/PopupActivityFilterPanel.web';
import {
  DATES_PANEL_WIDTH,
  PopupTaskFilterProjectDateRangeV3,
} from '@components/Web/PopupTaskFilterProjectV3.web';
import {
  GetProjectDocument,
  GetProjectUnreadAuditsDocument,
  useGetProjectUnreadAuditsQuery,
  useMarkAuditsReadMutation,
} from '@graphql/generated';
import { useDebouncedSearch } from '@hooks/useDebouncedSearch';
import { useOutsideClick } from '@hooks/useOutsideClick.web';
import { useProjectActivityFetch } from '@hooks/useProjectActivityFetch';
import { useProjectFromQuery } from '@hooks/useProjectFromQuery';
import { ProjectsStackScreenProps } from '@navigation/projects/projects-stack';
import { SEARCH_DEBOUNCE_MS } from '@src/constants/constants';
import { TAGS_UPDATED_EVENT } from '@utils/tagEvents';

const PER_PAGE_COUNT = 10;

const createProjectActivityLogWebListFooter =
  (canLoadMore: boolean, isLoadingMore: boolean) => () => (
    <>
      {canLoadMore ? (
        <Box mb='listFooter'>
          {isLoadingMore && <ActivityIndicatorLoading />}
        </Box>
      ) : (
        <Box mb='l' />
      )}
    </>
  );

const ProjectActivityLog: React.FC = () => {
  const {
    params: { projectUUId },
  } = useRoute<ProjectsStackScreenProps<'project-activity-log'>['route']>();

  const { t } = useTranslation();
  const isFocused = useIsFocused();

  const { project, loading: projectLoading } = useProjectFromQuery({
    projectUUId,
  });
  // TA-10616: deriving directly (rather than mirroring into state via an
  // effect) avoids an extra render where projectId is still undefined even
  // though `project` already resolved from cache — that extra render used to
  // reach the `!projectId` bail below and clear isLoading before the fetch
  // had a chance to run, flashing the "No search item found" empty state.
  const projectId = project?.id;

  // TA-10818: the hot-path getProject query/fragment (shared with
  // ProjectDetail and ~35 other consumers) only carries unreadAuditsCount
  // now, not the full unbounded list — this screen is the one place that
  // actually needs the ids, so it fetches them on its own, only when opened.
  // Gated on `project` (not just projectUUId) so a malformed route param
  // that useProjectFromQuery's own id/uuid validation already rejects can't
  // still reach the backend through this query (Copilot review, PR #5905).
  const { data: unreadAuditsData } = useGetProjectUnreadAuditsQuery({
    variables: { id: projectUUId || '' },
    skip: !projectUUId || !project,
    // network-only: this query exists only to feed markAuditsRead below with the
    // current ids -- the global cache-and-network default would resolve this
    // twice (once from a warm cache, once from the network), and the effect
    // below is keyed on the array reference, so a stale cache hit could fire
    // markAuditsRead for already-read ids before the real result lands
    // (Copilot review, PR #5905).
    fetchPolicy: 'network-only',
  });
  const unreadAudits = unreadAuditsData?.getProject?.unreadAudits;

  const [markAuditsRead] = useMarkAuditsReadMutation({
    refetchQueries: [
      ...(projectUUId
        ? [
            { query: GetProjectDocument, variables: { id: projectUUId } },
            // Otherwise the cached unread-audit ids survive the mark-read
            // call, and a later mount can briefly see the stale non-empty
            // list before the network refetch lands and re-fires
            // markAuditsRead for ids the backend already marked read
            // (Copilot review, PR #5905).
            {
              query: GetProjectUnreadAuditsDocument,
              variables: { id: projectUUId },
            },
          ]
        : []),
    ],
  });

  useEffect(() => {
    if ((unreadAudits?.length ?? 0) > 0) {
      markAuditsRead({
        variables: {
          attributes: { auditIds: unreadAudits?.map((a) => a.id) },
        },
      });
    }
  }, [unreadAudits, markAuditsRead]);

  // ─── Search state ────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');

  // ─── Filter state ────────────────────────────────────────────────────────────
  const [selectedUsers, setSelectedUsers] = useState<ContactType[]>([]);
  const [selectedDueDateRange, setSelectedDueDateRange] = useState<DateRange>({
    start: undefined,
    end: undefined,
  });
  const [isTimesheetModalOpen, setIsTimesheetModalOpen] = useState(false);
  const [isHoveredExportButton, setIsHoveredExportButton] = useState(false);

  // ─── Filter panel popup state ─────────────────────────────────────────────
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterPanel, setFilterPanel] = useState<'main' | 'dates'>('main');
  const [mouseOverFilter, setMouseOverFilter] = useState(false);

  const filterButtonRef = useOutsideClick<HTMLDivElement>(
    useCallback((event: MouseEvent) => {
      const rect = filterPanelRef.current?.getBoundingClientRect();
      const inPanel =
        rect &&
        event.x > rect.x &&
        event.x < rect.x + rect.width &&
        event.y > rect.y &&
        event.y < rect.y + rect.height;
      if (!inPanel) {
        setFilterVisible(false);
        setFilterPanel('main');
      }
    }, [])
  );
  const filterPanelRef = useRef<HTMLDivElement>();

  // Both popup panels are fixed-width (main = PANEL_WIDTH, dates =
  // DATES_PANEL_WIDTH), so we right-align them to the filter button using the
  // constant instead of rendering an off-screen copy on every page load just
  // to measure. (onLayout does not fire reliably for a child inside the
  // react-native-web Modal portal, so runtime measurement is not viable here.)
  const panelWidth = filterPanel === 'dates' ? DATES_PANEL_WIDTH : PANEL_WIDTH;

  const filterBounds = (() => {
    if (!filterButtonRef.current) return undefined;
    const client = filterButtonRef.current.getBoundingClientRect();
    return {
      top: client.top + client.height + 4,
      left: client.left + client.width - panelWidth,
    };
  })();

  const clearAllFilters = () => {
    setSelectedUsers([]);
    setSelectedDueDateRange({ start: undefined, end: undefined });
  };

  const badgeNumber =
    selectedUsers.length +
    (selectedDueDateRange.start || selectedDueDateRange.end ? 1 : 0);

  const userIds = useMemo(
    () => selectedUsers.map((u) => u.id),
    [selectedUsers]
  );

  // ─── Activity state ───────────────────────────────────────────────────────
  const {
    activityList,
    setActivityList,
    canLoadMore,
    setCanLoadMore,
    isLoading,
    setIsLoading,
    isLoadingMore,
    afterRef,
    isTruncated,
    doFetch: fetchActivities,
    invalidatePending,
  } = useProjectActivityFetch();

  // Value refs — keep doFetch stable while reading latest values
  const userIdsRef = useRef(userIds);
  const projectIdRef = useRef(projectId);
  const dateRangeRef = useRef(selectedDueDateRange);
  userIdsRef.current = userIds;
  projectIdRef.current = projectId;
  dateRangeRef.current = selectedDueDateRange;
  // Freezes the term actually backing the currently-loaded page(s) — set by
  // the fetch-trigger effect below at the moment it decides to fetch, and
  // read by doFetch for both the page-0 fetch and every load-more page.
  // Reading a live/raw search ref here instead caused wrong-page pagination
  // in Waves 1/3/4 when the user kept typing between a fetch and a
  // load-more scroll (TA-10170).
  const committedTermRef = useRef('');
  // Dedupes the below-threshold "browse" fetch so keystrokes that stay
  // below 3 chars (e.g. 'a' -> 'ab') don't each re-fire an identical
  // empty-term request — reset to null whenever an above-threshold search
  // is active so dropping back below threshold always refetches once
  // (TA-10170).
  const belowThresholdKeyRef = useRef<string | null>(null);

  // This screen's fetch-trigger effect below already owns debouncing and
  // fetching (it must coordinate with filter changes too), so onSearch here
  // is a no-op — useDebouncedSearch is used only for the shared 3-char
  // threshold gate (TA-10170).
  const { isBelowThreshold } = useDebouncedSearch(search, () => {});

  const doFetch = useCallback(
    async (after?: string) => {
      const currentProjectId = projectIdRef.current;
      if (!currentProjectId) return;
      await fetchActivities({
        projectId: currentProjectId,
        term: committedTermRef.current,
        userIds: userIdsRef.current.length > 0 ? userIdsRef.current : undefined,
        startDate: dateRangeRef.current.start
          ? dayjs(dateRangeRef.current.start).startOf('day').toISOString()
          : undefined,
        endDate: dateRangeRef.current.end
          ? dayjs(dateRangeRef.current.end).endOf('day').toISOString()
          : undefined,
        first: PER_PAGE_COUNT,
        after,
      });
    },
    [fetchActivities]
  );

  // Keep a ref so the effect below can always call the latest doFetch without
  // including it in the dependency array (avoids re-triggering on every render
  // when upstream hook functions don't have stable references).
  const doFetchRef = useRef(doFetch);
  doFetchRef.current = doFetch;

  const isFocusedRef = useRef(isFocused);
  isFocusedRef.current = isFocused;

  // Commits the current trimmed term (or clears it, below threshold) before
  // firing a page-0 fetch — every entrypoint that restarts the list from
  // page 0 outside the main fetch-trigger effect (tab-focus/tags-updated,
  // pull-to-refresh) must go through this instead of calling
  // doFetchRef.current() directly, or a refetch that lands while an
  // above-threshold term is still mid-debounce sends a stale/empty term
  // instead of the one the user is actively typing (TA-10170).
  const commitAndFetchPage0Ref = useRef(() => {});
  commitAndFetchPage0Ref.current = () => {
    committedTermRef.current = isBelowThreshold ? '' : search.trim();
    doFetchRef.current();
  };

  // Re-fetch when the tab regains focus to recover from stale data after a
  // WebSocket idle disconnect. Guard with isFocusedRef so screens mounted
  // but not active (e.g. a different tab is shown) don't trigger a fetch.
  // Also re-fetch when a message's tags are saved so the audit entry appears
  // immediately without requiring a manual refresh.
  useEffect(() => {
    const handleFocus = () => {
      if (isFocusedRef.current) commitAndFetchPage0Ref.current();
    };
    window.addEventListener('focus', handleFocus);
    window.addEventListener(TAGS_UPDATED_EVENT, handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener(TAGS_UPDATED_EVENT, handleFocus);
    };
  }, []);

  // Trigger fetch on any dependency change; debounce only for text search
  useEffect(() => {
    if (!projectId) {
      // TA-10616: only surrender the loading state once getProject has
      // actually settled. Clearing it while the project is still resolving
      // failed the `isLoading && activityList.length === 0` skeleton gate
      // below, so ActivityList rendered with zero audits and flashed "No
      // search item found" before any fetch had even been issued.
      if (!projectLoading) setIsLoading(false);
      return;
    }

    // Above the 3-char threshold: clear immediately so a stale previous
    // result can't linger on screen for the whole debounce window, but only
    // actually fetch once the debounce settles (TA-10170).
    if (!isBelowThreshold) {
      invalidatePending();
      afterRef.current = undefined;
      setActivityList([]);
      setCanLoadMore(false);
      setIsLoading(true);
      belowThresholdKeyRef.current = null;
      const debounced = debounce(() => {
        committedTermRef.current = search.trim();
        doFetchRef.current();
      }, SEARCH_DEBOUNCE_MS);
      debounced();
      return () => debounced.cancel();
    }

    // Below threshold (including empty input): browse the full list
    // immediately — no debounce, no partial-term remote search. Only
    // actually refetch when something that changes the result set changed
    // (filters/project) or we just dropped out of an active search — not on
    // every keystroke that stays within the below-threshold band (e.g.
    // 'a' -> 'ab' would otherwise re-fire an identical empty-term request
    // per keystroke).
    const belowThresholdKey = JSON.stringify([
      projectId,
      userIds,
      selectedDueDateRange.start,
      selectedDueDateRange.end,
    ]);
    if (belowThresholdKeyRef.current === belowThresholdKey) return;
    belowThresholdKeyRef.current = belowThresholdKey;

    afterRef.current = undefined;
    setActivityList([]);
    setCanLoadMore(false);
    setIsLoading(true);
    committedTermRef.current = '';
    doFetchRef.current();
    // doFetch intentionally excluded — all inputs are read via refs inside it
  }, [
    projectId,
    projectLoading,
    isBelowThreshold,
    search,
    userIds,
    selectedDueDateRange.start,
    selectedDueDateRange.end,
  ]);

  const onLoadMore = () => {
    if (isLoadingMore) return;
    doFetchRef.current(afterRef.current);
  };

  // TA-10792 QA fix: ActivityList passes this straight through to a
  // component-identity-sensitive prop, so an unmemoized factory/inline arrow
  // here hands a brand-new component type to the list on every unrelated
  // rerender and remounts the footer — same fix as the sibling native file.
  const listFooterComponent = useMemo(
    () => createProjectActivityLogWebListFooter(canLoadMore, isLoadingMore),
    [canLoadMore, isLoadingMore]
  );

  return (
    <Box flex={1}>
      {/* ─── Header ──────────────────────────────────────────────────────── */}
      <Box
        borderBottomWidth={1}
        borderBottomColor='grey02'
        flexDirection='row'
        alignItems='center'
        pt='xxs'
        pb='m'
        px='m'>
        {/* Search input */}
        <Box flex={1} mr='xs'>
          {isFocused ? (
            <SearchInput
              height={36}
              onTextChanged={setSearch}
              value={search}
              onCancel={() => setSearch('')}
            />
          ) : null}
        </Box>

        {/* Filter button */}
        <Box ref={filterButtonRef}>
          <TouchableOpacity
            accessibilityLabel={t('shared:filter')}
            onPress={() => {
              setFilterVisible((v) => !v);
              setFilterPanel('main');
            }}>
            <Box
              height={36}
              flexDirection='row'
              py='xs'
              px='m'
              onMouseEnter={() => setMouseOverFilter(true)}
              onMouseLeave={() => setMouseOverFilter(false)}
              backgroundColor={
                mouseOverFilter || badgeNumber > 0 || filterVisible
                  ? 'grey01'
                  : 'white'
              }
              borderRadius='xs'
              alignItems='center'
              justifyContent='center'>
              <Icon variant='s' name='AlignCenterDesc' color='black' />
              <Text marginLeft='xs' variant='labelSmall' color='textPrimary'>
                {t('shared:filter')}
              </Text>
              {badgeNumber > 0 && (
                <Box
                  marginLeft='xs'
                  minWidth={20}
                  height={20}
                  alignItems='center'
                  justifyContent='center'
                  backgroundColor='greenSecondary'
                  borderRadius='s'>
                  <Text variant='webBodyHintSubline' color='white'>
                    {badgeNumber}
                  </Text>
                </Box>
              )}
              {badgeNumber > 0 && (
                <Box>
                  <Icon
                    marginLeft='xs'
                    variant='xxxs'
                    name='Close'
                    onPress={(e) => {
                      e?.stopPropagation?.();
                      clearAllFilters();
                    }}
                    color='black'
                  />
                </Box>
              )}
            </Box>
          </TouchableOpacity>
        </Box>

        {/* Export timesheet button */}
        <TouchableOpacity
          accessibilityLabel='Export Timesheet'
          onPress={() => setIsTimesheetModalOpen(true)}>
          <Box
            height={36}
            flexDirection='row'
            py='xs'
            px='m'
            ml='xxxs'
            onMouseEnter={() => setIsHoveredExportButton(true)}
            onMouseLeave={() => setIsHoveredExportButton(false)}
            backgroundColor={isHoveredExportButton ? 'grey01' : 'white'}
            borderRadius='xs'
            alignItems='center'
            justifyContent='center'>
            <Icon variant='s' name='FolderPlus' color='black' />
            <Text marginLeft='xs' variant='labelSmall' color='textPrimary'>
              Export Timesheet
            </Text>
          </Box>
        </TouchableOpacity>

        {/* Filter panel modal */}
        <Modal
          visible={filterVisible}
          transparent
          onDismiss={() => {
            setFilterVisible(false);
            setFilterPanel('main');
          }}
          onRequestClose={() => {
            setFilterVisible(false);
            setFilterPanel('main');
          }}>
          <Box
            ref={filterPanelRef}
            style={{ left: filterBounds?.left, top: filterBounds?.top }}
            width={panelWidth}>
            <Box flexShrink={1}>
              {filterPanel === 'main' ? (
                <PopupActivityFilterPanel
                  showProjects={false}
                  selectedProjects={[]}
                  onSelectProject={() => {}}
                  selectedUsers={selectedUsers}
                  onSelectUser={setSelectedUsers}
                  selectedDueDateRange={selectedDueDateRange}
                  onPressSelectDates={() => setFilterPanel('dates')}
                  onClearAll={clearAllFilters}
                />
              ) : (
                <PopupTaskFilterProjectDateRangeV3
                  selectedDueDateRange={selectedDueDateRange}
                  onSelectDueDateRange={(dr) => {
                    setSelectedDueDateRange(dr);
                    setFilterPanel('main');
                  }}
                  isRangeSelector={true}
                />
              )}
            </Box>
          </Box>
        </Modal>
      </Box>

      {/* ─── Activity list ──────────────────────────────────────────────────── */}
      {isTruncated && <ActivityTruncationBanner />}
      {isLoading && activityList.length === 0 ? (
        <Box mb='listFooter'>
          <SkeletonList row={(index) => <ActivityRowSkeleton key={index} />} />
        </Box>
      ) : (
        <ActivityList
          activityType='Project'
          filterValForGlobalActivity={search || undefined}
          refreshing={false}
          audits={activityList}
          onEndReached={canLoadMore ? onLoadMore : undefined}
          projectId={projectId}
          onRefresh={() => {
            afterRef.current = undefined;
            setActivityList([]);
            setCanLoadMore(false);
            commitAndFetchPage0Ref.current();
          }}
          renderEmptyActiveComponent={() => (
            <Box
              alignItems='center'
              alignSelf='center'
              alignContent='center'
              justifyContent='center'>
              <Text color='grey04' textAlign='center'>
                {search
                  ? t('shared:noResultsFoundForTerm', { term: search })
                  : t('shared:NoRecordFound')}
              </Text>
            </Box>
          )}
          listFooterComponent={listFooterComponent}
        />
      )}

      <ExportActivityTimesheetModal
        visible={isTimesheetModalOpen}
        onClose={() => setIsTimesheetModalOpen(false)}
        projectId={projectId}
      />
    </Box>
  );
};

export default ProjectActivityLog;
